import React, { useEffect, useState } from 'react'
import { InView } from 'react-intersection-observer'
import { Button, LinkButton } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { HeadingSM, ParagraphSM, Text } from '@/atoms/Text'
import { useShopifyCart } from '@/services/Shopify/hooks'
import { useSafeTrack } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useShopifyUserAttributes } from '@/views/PreSales/hooks/useShopifyUserAttributes'
import { useTheatricalPromotionContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TheatricalPromotionContext'
import { GroupTicketsModalTypes } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal/GroupTicketsModal'
import { TicketOption } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal/TicketOption'

const GROUP_DISCOUNT_RATE = 0.5
const OPTIONS = [10, 20, 50, 100, 200]

interface Props {
  setModalType: React.Dispatch<React.SetStateAction<GroupTicketsModalTypes>>
  ticketPrice: number
  theatricalSlug: string
  groupTicketProductVariantId: string
  isLastRodeoPromotion?: boolean
}

export const ShopifyGroupTickets: React.FC<Props> = ({
  setModalType,
  ticketPrice,
  groupTicketProductVariantId,
  theatricalSlug,
  isLastRodeoPromotion = false,
}) => {
  const { t } = useTranslate('tickets')
  const track = useSafeTrack()
  const { query } = useSafeRouter()
  const { trackProductAdded, trackProductViewed } = useEcommerceEvents()
  const [selectedQuantity, setSelectedQuantity] = useState(OPTIONS[3])
  const [isProcessing, setIsProcessing] = useState(false)
  const { createCart, applyDiscountCode } = useShopifyCart()
  const [shopifyCheckoutUrl, setShopifyCheckoutUrl] = useState('')
  const { experiments } = useTheatricalPromotionContext()
  const attributes = useShopifyUserAttributes('theatrical-group-tickets', 'theatrical group tickets', theatricalSlug)
  const isLastRodeoGroupExp = experiments ? experiments['last-rodeo-group-tickets'] : undefined
  const expImageSrc = isLastRodeoGroupExp
    ? 'https://images.angelstudios.com/image/upload/v1744418920/the-last-rodeo/Neal_Cutout.png'
    : 'https://images.angelstudios.com/image/upload/v1744414123/the-last-rodeo/c7e2723c7531a72aae7c582e953f3f7b.png'

  useEffect(() => {
    if (!selectedQuantity) return

    setIsProcessing(true)
    const createShopifyCheckoutUrl = async () => {
      try {
        const { cart } = await createCart({
          lines: [
            {
              quantity: selectedQuantity,
              merchandiseId: `gid://shopify/ProductVariant/${groupTicketProductVariantId}`,
            },
          ],
          attributes,
        })

        if (!cart?.id) return null

        const discountCodeParam = getItemOrFirstEntry(query?.groupDiscountCode)
        const discountCode = discountCodeParam ? discountCodeParam : 'GROUPTICKETSDISCOUNT'

        const finalCart = await applyDiscountCode({ cartId: cart.id, discountCodes: [discountCode] })

        if (!finalCart?.cart?.checkoutUrl) return

        setShopifyCheckoutUrl(finalCart?.cart?.checkoutUrl)
      } catch (err) {
        logger().error('Failed to create shopifyCheckoutUrl for group tickets', err)
      } finally {
        setIsProcessing(false)
      }
    }

    createShopifyCheckoutUrl()
  }, [
    applyDiscountCode,
    attributes,
    createCart,
    groupTicketProductVariantId,
    query?.groupDiscountCode,
    selectedQuantity,
  ])

  useEffect(() => {
    track('Group Tickets Options Viewed')
  }, [track])

  const handleCtaClick = async (quantity: number) => {
    try {
      track('Group Tickets Checkout Started')

      trackProductAdded({
        category: 'group tickets',
        funnel: 'theatrical',
        name: `Group Ticket ${theatricalSlug}`,
        price: Number((ticketPrice * GROUP_DISCOUNT_RATE).toFixed(2)),
        currency: 'USD',
        quantity: quantity,
        product_id: `group-tickets-${theatricalSlug}`,
        sku: `group-tickets-${theatricalSlug}`,
        projectSlug: theatricalSlug,
      })
    } catch (err) {
      logger().error('Failed to create group ticket checkout', err)
    }
  }

  return (
    <InView
      triggerOnce
      onChange={(inView) => {
        if (!inView) return

        trackProductViewed({
          category: 'group tickets',
          funnel: 'theatrical',
          name: `Group Ticket ${theatricalSlug}`,
          price: Number((ticketPrice * GROUP_DISCOUNT_RATE).toFixed(2)),
          currency: 'USD',
          quantity: selectedQuantity,
          product_id: `group-tickets-${theatricalSlug}`,
          sku: `group-tickets-${theatricalSlug}`,
          url: '',
          projectSlug: theatricalSlug,
          value: selectedQuantity * ticketPrice * GROUP_DISCOUNT_RATE,
        })
      }}
    >
      {!isLastRodeoPromotion ? (
        <>
          <Text
            className="photon-heading-xs md:photon-heading-sm block text-center"
            color="core-gray-950"
            weight="bold"
          >
            {t('flexGroupTickets', 'Flex Group Tickets')}
          </Text>
          <Text
            color="core-gray-700"
            weight="medium"
            className="photon-paragraph-sm md:photon-paragraph-md mt-1 block pb-6 text-center"
          >
            <Translate t={t} i18nKey="youCanUseTheseTicketsOnAnySoundOfFreedomV6">
              Tickets only available at Angel Partner Theaters. Ticket prices estimated at $15. Additional charges may
              apply.{' '}
              <Button
                onClick={() => setModalType('faq')}
                className="inline font-semibold text-core-gray-950 underline"
                variant="plain"
              >
                Learn More
              </Button>
            </Translate>
          </Text>
        </>
      ) : (
        <>
          <div className={'text-center'}>
            <HeadingSM weight={'bold'} color={'core-gray-950'}>
              {t('groupTheBiggerTheCrew', 'The Bigger the Crew,')}
            </HeadingSM>
            <HeadingSM weight={'bold'} color={'core-gray-950'}>
              {t('groupTheBiggerTheSavings', 'The Bigger the Savings!')}
            </HeadingSM>
          </div>
          <ParagraphSM color={'core-gray-700'} className={'mt-2 text-center text-core-gray-700'}>
            <Translate t={t} i18nKey="groupRodeoTakeYourFamily">
              Take your family to the movies and earn free tickets. Use them to bring friends or family. Only at Angel
              Partner Theaters.
            </Translate>
          </ParagraphSM>
        </>
      )}

      <div className="mb-6 mt-4 flex justify-center">
        <form className="flex w-full flex-col gap-2" onSubmit={(e) => e.preventDefault()}>
          {groupTicketProductVariantId &&
            OPTIONS.map((quantity) => {
              const title = t('groupTicketTitle', '{{quantity}} Tickets', { quantity })

              const subTitle = t(
                'groupTicketDiscountExperimentSubtitlev2',
                'Buy {{paidQuantity}} → Get {{freeQuantity}} Free',
                {
                  paidQuantity: quantity / 2,
                  freeQuantity: quantity / 2,
                },
              )

              const isSelected = selectedQuantity === quantity
              return (
                <TicketOption
                  key={quantity}
                  onClick={() => setSelectedQuantity(quantity)}
                  title={title}
                  subTitle={subTitle}
                  ticketPrice={ticketPrice}
                  ticketQuantity={quantity}
                  selected={isSelected}
                />
              )
            })}
        </form>
      </div>

      {!isLastRodeoPromotion ? (
        <FlexColumn className="items-center gap-6">
          {shopifyCheckoutUrl ? (
            <LinkButton
              external
              href={shopifyCheckoutUrl}
              onClick={() => handleCtaClick(selectedQuantity)}
              className="mx-auto flex w-full max-w-[380px] items-center justify-center gap-1 rounded-[14px] px-7 py-[17px] md:w-fit"
              variant="black"
              disabled={isProcessing}
            >
              {isProcessing && <LoadingSpinnerIcon color="black" size={16} />}
              {t('checkoutGroupTickets', 'Checkout')}
            </LinkButton>
          ) : (
            <LoadingSpinnerIcon size={32} color="black" />
          )}
        </FlexColumn>
      ) : (
        <>
          <FlexColumn className="relative min-h-[135px] items-center gap-6">
            {shopifyCheckoutUrl ? (
              <LinkButton
                external
                href={shopifyCheckoutUrl}
                onClick={() => handleCtaClick(selectedQuantity)}
                className="mx-auto flex w-full max-w-[380px] items-center justify-center gap-1 rounded-[14px] px-7 py-[17px] md:w-fit"
                variant="black"
                disabled={isProcessing}
              >
                {isProcessing && <LoadingSpinnerIcon color="black" size={16} />}
                {t('checkoutGroupTickets', 'Checkout')}
              </LinkButton>
            ) : (
              <LoadingSpinnerIcon size={32} color="black" />
            )}
          </FlexColumn>
          <div
            className="pointer-events-none absolute -right-6 bottom-0"
            style={{
              backgroundImage: `url(${expImageSrc})`,
              backgroundPosition: 'top right',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '250px auto',
              width: '250px',
              height: '175px',
            }}
          />
        </>
      )}
    </InView>
  )
}
